import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Badge from "react-bootstrap/Badge";
import Alert from "react-bootstrap/Alert";
import {
  BookOpen,
  Plus,
  Edit3,
  Trash2,
  FileText,
  ChevronRight,
  Layers
} from "lucide-react";
import { getMe, getAdminModules, deleteAdminModule } from "../../api/client";
import { useTranslation } from "../../hooks/useTranslation";
import AIContentGenerator from "../../components/teacher/AIContentGenerator";

export default function ContentHub() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [modules, setModules] = useState([]);
  const [error, setError] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // TESTING MODE: Skip auth check
      const me = await getMe().catch(() => null);
      if (!me) {
        // Use mock user for testing
        setUser({ id: 1, user_type: 'teacher', full_name: 'Test Teacher' });
      } else {
        setUser(me);
      }

      const moduleList = await getAdminModules();
      setModules(moduleList || []);
    } catch (err) {
      console.error("ContentHub load error:", err);
      setError(t.contentCreator?.loadError || "Fehler beim Laden der Module");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (module) => {
    setModuleToDelete(module);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!moduleToDelete) return;

    setDeleting(true);
    try {
      await deleteAdminModule(moduleToDelete.id);
      setModules((prev) => prev.filter((m) => m.id !== moduleToDelete.id));
      setShowDeleteModal(false);
      setModuleToDelete(null);
    } catch (err) {
      console.error("Delete error:", err);
      setError(t.contentCreator?.deleteError || "Fehler beim Löschen");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">{t.common?.loading || "Lädt..."}</p>
      </Container>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <main
      className="content-hub-main"
      style={{
        backgroundColor: "var(--bg-secondary)",
        minHeight: "100vh",
      }}
    >
      <Container className="py-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="mb-1">
              <Layers size={28} className="me-2 text-primary" />
              {t.contentCreator?.title || "Inhalte verwalten"}
            </h1>
            <p className="text-muted mb-0">
              {t.contentCreator?.subtitle || "Module, Lektionen und Quiz erstellen und bearbeiten"}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate("/teacher/content/module/new")}
          >
            <Plus size={18} className="me-1" />
            {t.contentCreator?.newModule || "Neues Modul"}
          </Button>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* AI Content Generator */}
        <AIContentGenerator
          onContentGenerated={loadData}
          t={t}
          teacherSubjects={{
            rw: !!user?.has_rechnungswesen,
            bw: !!user?.has_betriebswirtschaft
          }}
        />

        {/* Module List */}
        <Row className="g-3">
          {modules.length === 0 ? (
            <Col xs={12}>
              <Card className="border-0 shadow-sm text-center py-5">
                <Card.Body>
                  <BookOpen size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">
                    {t.contentCreator?.noModules || "Noch keine Module vorhanden"}
                  </h5>
                  <p className="text-muted mb-3">
                    {t.contentCreator?.createFirst || "Erstellen Sie Ihr erstes Modul, um loszulegen."}
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate("/teacher/content/module/new")}
                  >
                    <Plus size={18} className="me-1" />
                    {t.contentCreator?.newModule || "Neues Modul"}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ) : (
            modules.map((module) => (
              <Col lg={6} xl={4} key={module.id}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <Badge bg="light" text="dark" className="mb-2">
                          {module.id}
                        </Badge>
                        <h5 className="mb-1">{module.title}</h5>
                      </div>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() =>
                            navigate(`/teacher/content/module/${module.id}`)
                          }
                          title={t.common?.edit || "Bearbeiten"}
                        >
                          <Edit3 size={14} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteClick(module)}
                          title={t.common?.delete || "Löschen"}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>

                    <p className="text-muted small mb-3">
                      {module.description || (t.contentCreator?.noDescription || "Keine Beschreibung")}
                    </p>

                    <div className="d-flex gap-2 mb-3">
                      <Badge bg="secondary">
                        {t.modules?.class || "Klasse"} {module.year_level}
                      </Badge>
                      <Badge bg="secondary">
                        {t.modules?.semester || "Semester"} {module.semester}
                      </Badge>
                      <Badge bg="info">
                        {module.lessonCount || 0} {t.modules?.lessons || "Lektionen"}
                      </Badge>
                    </div>

                    {/* Lesson List */}
                    {module.lessons && module.lessons.length > 0 && (
                      <div className="border-top pt-3">
                        <p className="small text-muted mb-2">
                          <FileText size={14} className="me-1" />
                          {t.contentCreator?.lessonsInModule || "Lektionen:"}
                        </p>
                        <div className="d-flex flex-column gap-1">
                          {module.lessons.slice(0, 3).map((lesson) => (
                            <div
                              key={lesson.id}
                              className="d-flex justify-content-between align-items-center small"
                            >
                              <span className="text-truncate" style={{ maxWidth: "70%" }}>
                                {lesson.title}
                              </span>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-secondary"
                                  onClick={() =>
                                    navigate(
                                      `/teacher/content/lesson/${module.id}/${lesson.id}`
                                    )
                                  }
                                  title={t.common?.edit || "Bearbeiten"}
                                >
                                  <Edit3 size={12} />
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-info"
                                  onClick={() =>
                                    navigate(
                                      `/teacher/content/quiz/${module.id}/${lesson.id}`
                                    )
                                  }
                                  title={t.contentCreator?.editQuiz || "Quiz bearbeiten"}
                                >
                                  <BookOpen size={12} />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {module.lessons.length > 3 && (
                            <span className="text-muted small">
                              +{module.lessons.length - 3} {t.contentCreator?.more || "weitere"}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-3 pt-2 border-top">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="w-100"
                        onClick={() =>
                          navigate(`/teacher/content/lesson/${module.id}/new`)
                        }
                      >
                        <Plus size={14} className="me-1" />
                        {t.contentCreator?.addLesson || "Lektion hinzufügen"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t.contentCreator?.confirmDelete || "Löschen bestätigen"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t.contentCreator?.deleteModuleConfirm ||
              "Möchten Sie dieses Modul wirklich löschen? Alle Lektionen und Quiz werden ebenfalls gelöscht."}
          </p>
          {moduleToDelete && (
            <p className="fw-bold mb-0">
              {moduleToDelete.title} ({moduleToDelete.id})
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            {t.common?.cancel || "Abbrechen"}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                {t.common?.loading || "Lädt..."}
              </>
            ) : (
              t.common?.delete || "Löschen"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}
